import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from './authcontext';
import { runAgentQuery } from '../services/aiAgent';
import logger from '../logger';
import './AskVik.css';

const AskVik = () => {
  const { token } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copiedMessageId, setCopiedMessageId] = useState(null);
  const messagesContainerRef = useRef(null);
  const inputRef = useRef(null);
  const abortControllerRef = useRef(null);

  const suggestions = [
    { text: "Give me a full dashboard overview — users, courses, and key metrics", icon: "star" },
    { text: "How many active users do we have and what percentage is that?", icon: "users" },
    { text: "Which courses have the most enrollments and best completion rates?", icon: "courses" },
    { text: "Show me recent activity logs — what's been happening on the platform?", icon: "activity" },
    { text: "Are there any pending certificate requests that need review?", icon: "alert" },
    { text: "How are our quizzes performing? Show average scores and completion stats", icon: "trend" },
  ];

  // Get messages for the active conversation
  const activeConversation = conversations.find(c => c.id === activeConversationId);
  const messages = activeConversation?.messages || [];

  // Create a new conversation
  const createNewConversation = useCallback(() => {
    // Cancel any pending request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setIsLoading(false);
    setError(null);

    const newConversation = {
      id: Date.now(),
      title: 'New Conversation',
      messages: [],
      createdAt: new Date(),
    };
    setConversations(prev => [newConversation, ...prev]);
    setActiveConversationId(newConversation.id);
    setInputValue('');
    inputRef.current?.focus();
  }, []);

  // Initialize with one conversation on mount
  useEffect(() => {
    if (conversations.length === 0) {
      createNewConversation();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const scrollToBottom = useCallback(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading, scrollToBottom]);

  // Cleanup abort controller on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const handleSuggestionClick = (suggestion) => {
    setInputValue(suggestion);
    inputRef.current?.focus();
  };

  const addMessageToConversation = (conversationId, message) => {
    setConversations(prev =>
      prev.map(conv => {
        if (conv.id !== conversationId) return conv;
        const updatedMessages = [...conv.messages, message];
        // Update conversation title from first user message
        const title = conv.messages.length === 0 && message.role === 'user'
          ? message.content.slice(0, 50) + (message.content.length > 50 ? '...' : '')
          : conv.title;
        return { ...conv, messages: updatedMessages, title };
      })
    );
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!inputValue.trim() || isLoading) return;

    const userContent = inputValue.trim();
    const conversationId = activeConversationId;

    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: userContent,
      timestamp: new Date(),
    };

    addMessageToConversation(conversationId, userMessage);
    setInputValue('');
    setIsLoading(true);
    setError(null);

    // Build conversation history for context
    const currentConv = conversations.find(c => c.id === conversationId);
    const history = (currentConv?.messages || []).map(m => ({
      role: m.role,
      content: m.content,
    }));
    history.push({ role: 'user', content: userContent });

    // Create abort controller for this request
    abortControllerRef.current = new AbortController();

    try {
      const response = await runAgentQuery(
        userContent,
        history,
        abortControllerRef.current.signal
      );

      const assistantMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        content: response.message || 'I received your message but got an unexpected response format.',
        timestamp: new Date(),
      };

      addMessageToConversation(conversationId, assistantMessage);
    } catch (err) {
      if (err.name === 'AbortError' || err.name === 'CanceledError') return;

      logger.error('Assistant API error:', err);

      const statusCode = err.response?.status || err.status;
      let errorMessage;

      if (statusCode === 429) {
        errorMessage = 'You\'re sending messages too quickly. Please wait a moment and try again.';
      } else if (statusCode === 401 || statusCode === 403) {
        errorMessage = 'Your session has expired. Please log in again.';
      } else if (statusCode >= 500) {
        errorMessage = 'The server encountered an error. Please try again in a few moments.';
      } else if (!navigator.onLine) {
        errorMessage = 'You appear to be offline. Please check your connection and try again.';
      } else {
        errorMessage = err.response?.data?.message || err.message || 'Something went wrong. Please try again.';
      }

      setError(errorMessage);

      // Add error as a system message so the user sees it in context
      const errorMsg = {
        id: Date.now() + 1,
        role: 'assistant',
        content: errorMessage,
        timestamp: new Date(),
        isError: true,
      };
      addMessageToConversation(conversationId, errorMsg);
    } finally {
      setIsLoading(false);
      abortControllerRef.current = null;
    }
  };

  const handleClearChat = () => {
    if (!activeConversation || activeConversation.messages.length === 0) return;
    setConversations(prev =>
      prev.map(conv =>
        conv.id === activeConversationId
          ? { ...conv, messages: [], title: 'New Conversation' }
          : conv
      )
    );
    setError(null);
    inputRef.current?.focus();
  };

  const handleCopyMessage = async (messageId, content) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedMessageId(messageId);
      setTimeout(() => setCopiedMessageId(null), 2000);
    } catch (err) {
      logger.warn('Failed to copy to clipboard:', err);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend(e);
    }
  };

  const formatTime = (date) => {
    return new Intl.DateTimeFormat('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(date));
  };

  // Simple markdown renderer for assistant messages
  const renderMarkdown = (text) => {
    if (!text) return null;

    const lines = text.split('\n');
    const elements = [];
    let i = 0;

    while (i < lines.length) {
      const line = lines[i];

      // Table detection: line contains | and next line is separator
      if (line.includes('|') && i + 1 < lines.length && /^\s*\|?[\s-:|]+\|/.test(lines[i + 1])) {
        const tableLines = [];
        while (i < lines.length && lines[i].includes('|')) {
          tableLines.push(lines[i]);
          i++;
        }
        elements.push(renderTable(tableLines, elements.length));
        continue;
      }

      // Code block
      if (line.trim().startsWith('```')) {
        const lang = line.trim().replace('```', '').trim();
        const codeLines = [];
        i++;
        while (i < lines.length && !lines[i].trim().startsWith('```')) {
          codeLines.push(lines[i]);
          i++;
        }
        i++; // skip closing ```
        elements.push(
          <div key={elements.length} className="vik-code-block">
            {lang && <div className="vik-code-lang">{lang}</div>}
            <pre><code>{codeLines.join('\n')}</code></pre>
          </div>
        );
        continue;
      }

      // Headers
      if (line.startsWith('### ')) {
        elements.push(<h4 key={elements.length} className="vik-md-h3">{renderInline(line.slice(4))}</h4>);
        i++;
        continue;
      }
      if (line.startsWith('## ')) {
        elements.push(<h3 key={elements.length} className="vik-md-h2">{renderInline(line.slice(3))}</h3>);
        i++;
        continue;
      }
      if (line.startsWith('# ')) {
        elements.push(<h2 key={elements.length} className="vik-md-h1">{renderInline(line.slice(2))}</h2>);
        i++;
        continue;
      }

      // Unordered list
      if (/^\s*[-*]\s/.test(line)) {
        const listItems = [];
        while (i < lines.length && /^\s*[-*]\s/.test(lines[i])) {
          listItems.push(lines[i].replace(/^\s*[-*]\s/, ''));
          i++;
        }
        elements.push(
          <ul key={elements.length} className="vik-md-list">
            {listItems.map((item, idx) => (
              <li key={idx}>{renderInline(item)}</li>
            ))}
          </ul>
        );
        continue;
      }

      // Ordered list
      if (/^\s*\d+\.\s/.test(line)) {
        const listItems = [];
        while (i < lines.length && /^\s*\d+\.\s/.test(lines[i])) {
          listItems.push(lines[i].replace(/^\s*\d+\.\s/, ''));
          i++;
        }
        elements.push(
          <ol key={elements.length} className="vik-md-list vik-md-ol">
            {listItems.map((item, idx) => (
              <li key={idx}>{renderInline(item)}</li>
            ))}
          </ol>
        );
        continue;
      }

      // Empty line
      if (line.trim() === '') {
        i++;
        continue;
      }

      // Regular paragraph
      elements.push(<p key={elements.length} className="vik-md-p">{renderInline(line)}</p>);
      i++;
    }

    return elements;
  };

  // Render inline markdown (bold, italic, code, links)
  const renderInline = (text) => {
    if (!text) return null;
    const parts = [];
    // Match bold, italic, inline code, and metric highlights
    const regex = /(\*\*(.+?)\*\*)|(`(.+?)`)|(\*(.+?)\*)|(\[(.+?)\]\((.+?)\))/g;
    let lastIndex = 0;
    let match;

    while ((match = regex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push(text.slice(lastIndex, match.index));
      }
      if (match[1]) {
        // Bold
        parts.push(<strong key={match.index}>{match[2]}</strong>);
      } else if (match[3]) {
        // Inline code
        parts.push(<code key={match.index} className="vik-inline-code">{match[4]}</code>);
      } else if (match[5]) {
        // Italic
        parts.push(<em key={match.index}>{match[6]}</em>);
      } else if (match[7]) {
        // Link - render as plain text (no external nav from admin panel)
        parts.push(<span key={match.index} className="vik-link">{match[8]}</span>);
      }
      lastIndex = match.index + match[0].length;
    }
    if (lastIndex < text.length) {
      parts.push(text.slice(lastIndex));
    }
    return parts.length > 0 ? parts : text;
  };

  // Render markdown table
  const renderTable = (tableLines, key) => {
    const rows = tableLines
      .filter(line => !/^\s*\|?[\s-:|]+\|?\s*$/.test(line)) // filter separator rows
      .map(line =>
        line.split('|').map(cell => cell.trim()).filter(cell => cell !== '')
      );

    if (rows.length === 0) return null;
    const header = rows[0];
    const body = rows.slice(1);

    return (
      <div key={key} className="vik-table-wrapper">
        <table className="vik-table">
          <thead>
            <tr>
              {header.map((cell, idx) => (
                <th key={idx}>{renderInline(cell)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {body.map((row, rowIdx) => (
              <tr key={rowIdx}>
                {row.map((cell, cellIdx) => (
                  <td key={cellIdx}>{renderInline(cell)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // Icon components for suggestion chips
  const SuggestionIcon = ({ type }) => {
    const icons = {
      users: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M16 4c0-1.11.89-2 2-2s2 .89 2 2-.89 2-2 2-2-.89-2-2zm4 18v-6h2.5l-2.54-7.63A1.5 1.5 0 0 0 18.54 8H17c-.8 0-1.54.37-2.01 1l-1.74 1.96c-.34.41-.54.93-.54 1.48V18h-2v-6c0-1.1.45-2.11 1.17-2.83L15 7.83c.39-.39.9-.59 1.41-.59h2.12c.72 0 1.39.35 1.81.94L21.5 16H19v6h1z" />
        </svg>
      ),
      trend: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M16 6l2.29 2.29-4.88 4.88-4-4L2 16.59 3.41 18l6-6 4 4 6.3-6.29L22 12V6z" />
        </svg>
      ),
      activity: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M13 3c-4.97 0-9 4.03-9 9H1l3.89 3.89.07.14L9 12H6c0-3.87 3.13-7 7-7s7 3.13 7 7-3.13 7-7 7c-1.93 0-3.68-.79-4.94-2.06l-1.42 1.42C8.27 19.99 10.51 21 13 21c4.97 0 9-4.03 9-9s-4.03-9-9-9zm-1 5v5l4.28 2.54.72-1.21-3.5-2.08V8H12z" />
        </svg>
      ),
      courses: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
        </svg>
      ),
      alert: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z" />
        </svg>
      ),
      star: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z" />
        </svg>
      ),
    };
    return icons[type] || null;
  };

  return (
    <div className="ask-vik-page" role="main" aria-label="EnRiCH DLP Assistant">
      {/* Header */}
      <div className="ask-vik-header">
        <div className="vik-avatar" aria-hidden="true">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
            <circle cx="9" cy="9" r="1.5"/>
            <circle cx="15" cy="9" r="1.5"/>
            <path d="M9 15h6"/>
            <path d="M12 3v3"/>
            <path d="M12 18v3"/>
            <path d="M3 12h3"/>
            <path d="M18 12h3"/>
          </svg>
        </div>
        <div className="vik-header-info">
          <h1>EnRiCH DLP Assistant</h1>
          <p>AI-powered insights for your admin dashboard</p>
        </div>
        <div className="vik-header-actions">
          <button
            className="vik-action-btn"
            onClick={createNewConversation}
            title="New Conversation"
            aria-label="Start a new conversation"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            <span>New Chat</span>
          </button>
          <button
            className="vik-action-btn vik-action-btn-secondary"
            onClick={handleClearChat}
            disabled={messages.length === 0}
            title="Clear current conversation"
            aria-label="Clear current conversation"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
            </svg>
            <span>Clear</span>
          </button>
        </div>
        <div className="vik-status">
          <span className="status-dot"></span>
          <span>Online</span>
        </div>
      </div>

      {/* Chat Container */}
      <div className="ask-vik-container">
        <div
          className="messages-container"
          ref={messagesContainerRef}
          role="log"
          aria-live="polite"
          aria-label="Chat messages"
        >
          {/* Empty state with welcome message */}
          {messages.length === 0 && (
            <div className="vik-welcome">
              <div className="vik-welcome-icon" aria-hidden="true">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                  <circle cx="9" cy="9" r="1.5"/>
                  <circle cx="15" cy="9" r="1.5"/>
                  <path d="M9 15h6"/>
                  <path d="M12 3v3"/>
                  <path d="M12 18v3"/>
                  <path d="M3 12h3"/>
                  <path d="M18 12h3"/>
                </svg>
              </div>
              <h2>Welcome to EnRiCH DLP Assistant</h2>
              <p>
                Ask me anything about your platform — users, courses, enrollments,
                activity, metrics, and more. I can query the system and provide
                insights in real time.
              </p>

              <div className="suggestions-container">
                <div className="suggestions-header">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                    <line x1="12" y1="17" x2="12.01" y2="17" />
                  </svg>
                  <span>Try asking:</span>
                </div>
                <div className="suggestions-grid">
                  {suggestions.map((suggestion, index) => (
                    <button
                      key={index}
                      className="suggestion-chip"
                      onClick={() => handleSuggestionClick(suggestion.text)}
                      type="button"
                      aria-label={`Ask: ${suggestion.text}`}
                    >
                      <span className="suggestion-chip-icon">
                        <SuggestionIcon type={suggestion.icon} />
                      </span>
                      <span className="suggestion-chip-text">{suggestion.text}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Messages */}
          {messages.map((message) => (
            <div
              key={message.id}
              className={`message ${message.role === 'assistant' ? 'bot-message' : 'user-message'} ${message.isError ? 'error-message' : ''}`}
              role="article"
              aria-label={`${message.role === 'assistant' ? 'Assistant' : 'You'} said`}
            >
              {message.role === 'assistant' && (
                <div className="message-avatar bot-avatar" aria-hidden="true">
                  {message.isError ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="15" y1="9" x2="9" y2="15" />
                      <line x1="9" y1="9" x2="15" y2="15" />
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                      <circle cx="9" cy="9" r="1.5"/>
                      <circle cx="15" cy="9" r="1.5"/>
                      <path d="M9 15h6"/>
                    </svg>
                  )}
                </div>
              )}
              <div className="message-content">
                <div className={`message-text ${message.role === 'assistant' ? 'vik-md-content' : ''}`}>
                  {message.role === 'assistant'
                    ? renderMarkdown(message.content)
                    : message.content
                  }
                </div>
                <div className="message-footer">
                  <span className="message-time">{formatTime(message.timestamp)}</span>
                  {message.role === 'assistant' && !message.isError && (
                    <button
                      className={`copy-btn ${copiedMessageId === message.id ? 'copied' : ''}`}
                      onClick={() => handleCopyMessage(message.id, message.content)}
                      title={copiedMessageId === message.id ? 'Copied!' : 'Copy response'}
                      aria-label={copiedMessageId === message.id ? 'Copied to clipboard' : 'Copy response to clipboard'}
                    >
                      {copiedMessageId === message.id ? (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      ) : (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                        </svg>
                      )}
                      <span>{copiedMessageId === message.id ? 'Copied' : 'Copy'}</span>
                    </button>
                  )}
                </div>
              </div>
              {message.role === 'user' && (
                <div className="message-avatar user-avatar" aria-hidden="true">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                  </svg>
                </div>
              )}
            </div>
          ))}

          {/* Loading indicator */}
          {isLoading && (
            <div className="message bot-message" role="status" aria-label="Assistant is thinking">
              <div className="message-avatar bot-avatar" aria-hidden="true">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                  <circle cx="9" cy="9" r="1.5"/>
                  <circle cx="15" cy="9" r="1.5"/>
                  <path d="M9 15h6"/>
                </svg>
              </div>
              <div className="message-content">
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Error banner (dismissible) */}
        {error && (
          <div className="vik-error-banner" role="alert">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <span>{error}</span>
            <button
              className="vik-error-dismiss"
              onClick={() => setError(null)}
              aria-label="Dismiss error"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        )}

        {/* Input */}
        <form className="input-container" onSubmit={handleSend}>
          <div className="input-wrapper">
            <textarea
              ref={inputRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask anything about users, courses, enrollments, activity..."
              className="message-input"
              disabled={isLoading}
              rows={1}
              aria-label="Type your message"
            />
            <button
              type="submit"
              className="send-button"
              disabled={!inputValue.trim() || isLoading}
              aria-label="Send message"
            >
              {isLoading ? (
                <div className="send-spinner" aria-hidden="true"></div>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="22" y1="2" x2="11" y2="13" />
                  <polygon points="22 2 15 22 11 13 2 9 22 2" />
                </svg>
              )}
            </button>
          </div>
          <div className="input-hint">
            Press Enter to send, Shift+Enter for new line
          </div>
        </form>
      </div>
    </div>
  );
};

export default AskVik;
